import type { components } from '@app/api/types';
import { t } from '@app/i18n';
import { dataHour } from '@app/format';

/**
 * T13 — o contrato vigente em texto (FR-069, FR-070) e os rótulos do card
 * Estado.
 */

export type Contract = components['schemas']['Contract'];
export type ContractView = components['schemas']['ContractView'];
export type ContractSummary = components['schemas']['ContractSummary'];
export type Capability = components['schemas']['Capability'];
export type Identity = components['schemas']['Identity'];
export type PolicyThatGrants = ContractView['granting_policies'][number];

/**
 * Uma capability é de UMA das duas espécies desde o DEF-29 (09/09): `tool` é
 * permissão de usar uma tool de um resource; `agent` é uma **delegação** — a
 * identidade alvo que esta pode invocar.
 *
 * `kind` ausente lê como `tool`: era a única espécie até 08/09, e é assim que o
 * contrato manda ler quem não conhece o campo.
 */
export function isDelegation(cap: Capability): boolean {
  return cap.kind === 'agent';
}

/** As tools do contrato — a lista filtrada, nunca uma segunda origem. */
export function contractTools(contrato: Contract): Capability[] {
  return contrato.capabilities.filter((cap) => !isDelegation(cap));
}

/**
 * As delegações do contrato.
 *
 * Lê de `capabilities`, **não** do campo `a2a` — que continua emitido durante a
 * janela de quebra e está marcado deprecado no contrato. São a mesma lista
 * filtrada; ler a deprecada deixaria a tela dependendo do dia em que ela sair.
 */
export function contractDelegations(contrato: Contract): Capability[] {
  return contrato.capabilities.filter(isDelegation);
}

/**
 * O rótulo de uma delegação: o slug da identidade do agente alvo; sem ele, o nome do agente, e depois o caminho dele
 * no gateway. A reserva do catálogo fica para o alvo sem nada disso (vazio também falta).
 *
 * É o MESMO rótulo no contrato de Security Contracts (T13) e na aba Rules da identidade (aim-903): a regra e o contrato
 * falam do mesmo alvo, e duas formas de escrevê-lo fariam a pessoa procurar duas coisas. O nome e o caminho entram
 * por causa da regra: ela pode delegar a um agente externo, que não tem identidade no console, e o «(alvo sem slug)»
 * esconderia um alvo que a resposta já nomeia.
 */
export function delegationLabel(target: {
  target_slug?: string | null;
  target_name?: string | null;
  target_path?: string | null;
}): string {
  return target.target_slug || target.target_name || target.target_path || t('contracts.block.delegationNoSlug');
}

/**
 * `mcp-nativo/credito.aplicar [max_amount ≤ 50]` — como o protótipo escreve; na
 * delegação, o slug do alvo.
 *
 * Uma delegação não tem resource, tool, limite nem risco: o que ela concede é o
 * alvo inteiro. Escrever `undefined/undefined` para ela — o que a versão anterior
 * a esta função faria — seria a tela afirmando um par que não existe.
 */
export function capabilityLabel(cap: Capability): string {
  if (isDelegation(cap)) return delegationLabel(cap);
  const nome = `${cap.resource_gateway_id}/${cap.tool_name}`;
  const limits = Object.entries(cap.constraints ?? {});
  if (!limits.length) return nome;
  return `${nome} [${limits.map(([chave, valor]) => `${chave} ≤ ${valor}`).join(', ')}]`;
}

const WIDTH = 18;
const RECUO = '\n' + ' '.repeat(WIDTH);

// Rótulo mais largo que a coluna («Namespace / Workload») ganha um espaço em vez
// de colar no valor; alargar a coluna mexeria em toda linha que o e2e de T13 lê.
function linha(rotulo: string, valor: string): string {
  return `${`${rotulo}:`.padEnd(Math.max(WIDTH, rotulo.length + 2))}${valor}\n`;
}

function block(rotulo: string, itens: string[], empty: string): string {
  const corpo = itens.length ? itens.join(RECUO) : empty;
  return `\n${linha(rotulo, corpo)}`;
}

/**
 * O contrato vigente como texto copiável — as seções que FR-069 lista, com os
 * rótulos em pt-BR da tela T13 do PRD (Identidade · Papel · Especialidade ·
 * Release · Versão · Imagem · Namespace / Workload · SPIFFE ID · Pode
 * invocar · Pode usar · Credenciais declaradas · Regras de Human Access ·
 * Aprovado por · quando · motivo). O protótipo escrevia em inglês
 * (`contractText`); a linha `Network: gateway-only` dele sai (PRD §4 "Sai do
 * protótipo": depende de Q4).
 *
 * É montado aqui e não copiado de `vigente.texto` porque a FR-070 manda o texto
 * incluir também as **regras de Human Access que mudam o que vale**, e essas não
 * fazem parte do contrato: chegam ao lado, em `granting_policies`.
 *
 * A seção deixou de ser «o que a política concede a mais» em 03/09 — a regra não
 * alarga a identidade. Desde 04/09 ela lista as regras que ALCANÇAM a
 * identidade (cobrem e concedem tool que o contrato expõe), com «recorta teto»
 * na que define o limite em vigor: é a regra que afirma o eixo pessoa, e sem ela
 * o PDP nega, mesmo que nenhum número mude.
 */
export function contractInText(view: ContractView, contrato: Contract | null): string {
  const identidade = view.identity;
  const nome = identidade.slug ?? identidade.image_basename ?? '—';

  if (!contrato) {
    return [
      linha(t('contracts.field.identity'), nome),
      linha(t('contracts.field.role'), identidade.role ?? '—'),
      linha(t('contracts.field.release'), t('contracts.field.releaseNone')),
      linha(t('contracts.field.version'), '—'),
      linha(t('contracts.field.image'), identidade.image ?? '—'),
      linha(t('contracts.field.spiffeId'), identidade.spiffe_id_predicted ?? '—'),
      `\n${t('contracts.block.withoutContract')}`,
    ].join('');
  }

  const payload = contrato.identity;
  let texto = linha(t('contracts.field.identity'), payload?.slug ?? nome);
  texto += linha(t('contracts.field.role'), payload?.role ?? identidade.role ?? '—');
  texto += linha(t('contracts.field.specialty'), payload?.specialty ?? '—');
  texto += linha(t('contracts.field.release'), contrato.digest);
  texto += linha(t('contracts.field.version'), `v${contrato.version}`);
  texto += linha(t('contracts.field.commit'), contrato.commit ?? '—');
  texto += linha(t('contracts.field.image'), payload?.image ?? identidade.image ?? '—');
  texto += linha(
    t('contracts.field.namespaceWorkload'),
    `${payload?.namespace ?? '—'}/${payload?.workload || '—'}`,
  );
  // O ID CONGELADO no contrato (aim-xsk.7, W2), nunca o previsto de hoje: o template
  // do site pode ter mudado depois da aprovação. Contrato anterior ao modo workload
  // não tem ID congelado, e mostrar o previsto afirmaria um ID que ele nunca teve.
  texto += linha(t('contracts.field.spiffeId'), payload?.spiffe_id || '—');

  // DEF-29 (09/09): as duas seções saem da MESMA lista, separadas por `kind`.
  // «Pode invocar» lia `contrato.a2a`, que saiu do contrato na quebra de 09/09.
  texto += block(
    t('contracts.block.canInvoke'),
    contractDelegations(contrato).map(capabilityLabel),
    t('contracts.block.canInvokeEmpty'),
  );
  texto += block(
    t('contracts.block.canUse'),
    contractTools(contrato).map(capabilityLabel),
    t('contracts.block.canUseEmpty'),
  );
  texto += block(
    t('contracts.block.credentials'),
    contrato.credentials,
    t('contracts.block.credentialsEmpty'),
  );

  const regras = view.granting_policies.flatMap((regra) => [
    `· ${regra.name ?? t('contracts.block.ruleFallbackName')}` +
      (regra.tightens_contract ? t('contracts.block.tightensCap') : ''),
    ...(regra.capabilities ?? []).map((cap) => `    ${capabilityLabel(cap)}`),
  ]);
  texto += block(
    t('contracts.block.humanAccessRules'),
    regras,
    t('contracts.block.humanAccessRulesEmpty'),
  );

  texto += `\n${linha(t('contracts.field.approvedBy'), contrato.approved_by)}`;
  texto += linha(t('contracts.field.when'), dataHour(contrato.approved_at) ?? contrato.approved_at);
  texto += linha(t('contracts.field.reason'), contrato.decision_reason || '—');

  return texto;
}

/** Publicação no PDP (FR-070, FR-088) — três estados, nunca dois. */
export function pdpLabel(estado: ContractView['state']): {
  rotulo: string;
  tone: 'success' | 'danger' | 'neutral';
} {
  if (estado.error_code) return { rotulo: t('contracts.pdp.notRead'), tone: 'neutral' };
  if (estado.pdp_up_to_date === true) return { rotulo: t('contracts.pdp.upToDate'), tone: 'success' };
  if (estado.pdp_up_to_date === false) {
    return { rotulo: t('contracts.pdp.diverging'), tone: 'danger' };
  }
  return { rotulo: t('contracts.pdp.notRead'), tone: 'neutral' };
}

/** SecurityReady é tri-estado: sim, não, ou ainda não sabemos. */
export function securityReadyLabel(valor: components['schemas']['TriEstado'] | undefined): {
  rotulo: string;
  tone: 'success' | 'danger' | 'neutral';
} {
  if (valor === true) return { rotulo: t('contracts.securityReady.ready'), tone: 'success' };
  if (valor === false) return { rotulo: t('contracts.securityReady.notReady'), tone: 'danger' };
  return { rotulo: t('contracts.securityReady.notEvaluated'), tone: 'neutral' };
}
